/* eslint-disable max-len */
import React, { CSSProperties, useContext, useMemo } from 'react';
import { RouteProps, useParams, useNavigate, Navigate } from 'react-router-dom';
import { Agent, CustomFeature, hasAccess, Permission } from '@moderepo/mode-internal-apis';
import { ritzDataContext, selectLoggedInAgent, selectProjectAgent, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { PageContainer } from '../page/PageContainer';
import { Layout } from '../layout/Layout';
import { RouteHelper } from './RouteHelper';
import { ProjectTypeHandlersManager } from '../../utils/ritzExtensionUtils';


export interface RitzProjectRouteProps extends RouteProps {
    readonly pageProps?: {
        readonly className?: string | undefined;
        readonly style?: CSSProperties | undefined;
    } | undefined;
    readonly layoutProps?: unknown | undefined;
    readonly requiredPermissions?: Permission[] | undefined;
    readonly customFeature?: CustomFeature | undefined;
}


export const RitzProjectRoute: React.FC<RitzProjectRouteProps> = (props: RitzProjectRouteProps) => {

    // Get the 'element' and requiredPermissions array from the route's props
    // and everything else goes into the 'otherProps' array
    const {
        element: Element,
        layoutProps,
        pageProps,
        requiredPermissions,
        customFeature,
    } = props;


    const navigate = useNavigate();
    const params = useParams();
    const { state:dataState } = useContext(ritzDataContext);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    const projectId = Number(params.projectId);
    const project = projectId ? selectProjectById(dataState, projectId) : undefined;
    const projectAgent = projectId && loggedInAgent ? selectProjectAgent(dataState, projectId, loggedInAgent.id) : undefined;

    const routeHelper = RouteHelper.getInstance(navigate);


    // Get the page layout based on the current project's type
    const pageLayout = useMemo((): React.FC => {
        const pageLayoutHandler = ProjectTypeHandlersManager.getInstance().getPageLayoutHandler(project?.supportToolSettings.projectType);
        if (pageLayoutHandler && pageLayoutHandler.getPageLayoutComponent) {
            return pageLayoutHandler.getPageLayoutComponent(project, loggedInAgent) || Layout;
        }

        return Layout;
    }, [loggedInAgent, project]);


    // Check to see if the agent has permission to access this page
    if (project && projectAgent && !hasAccess(projectAgent, project, requiredPermissions, customFeature)) {
        return (
            <Navigate to={routeHelper.getNotAuthorizedPageUrl()} />
        );
    }

    return (
        <PageContainer projectId={projectId} layoutComponent={pageLayout || Layout} {...pageProps} layoutProps={layoutProps}>
            {Element}
        </PageContainer>
    );
};
